import ChevronDown from '@assets/icons/chevron-down.svg';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Button } from './button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';

export interface PillButtonType {
  active: boolean;
  collapsed?: boolean;
  count?: number;
  dropDown?: boolean;
  label: string;
  onClear?: () => void;
  onPress: () => void;
  ref?: React.RefObject<View | null>;
  style?: StyleProp<ViewStyle>;
}

interface Props {
  button: PillButtonType;
  onLayout?: ((event: LayoutChangeEvent) => void) | undefined;
  style?: StyleProp<ViewStyle>;
}

export const PillButton = forwardRef<View, Props>(
  ({ button, onLayout, style }, ref) => {
    const [styles, theme] = useStyles();
    const collapsedAnimation = useRef(
      new Animated.Value(button.collapsed ? 0 : 1),
    ).current;
    const [animatedWidth, setAnimatedWidth] = useState<string[] | number[]>([
      '0%',
      '100%',
    ]);

    useEffect(() => {
      Animated.timing(collapsedAnimation, {
        duration: 200,
        toValue: button.collapsed ? 0 : 1,
        useNativeDriver: false,
      }).start();
    }, [collapsedAnimation, button.collapsed]);

    // Anchor point for the scaling up/down animation
    const [anchor, setAnchor] = useState({ x: 0, y: 0 });

    // Calculate the anchor point based on the button's layout
    const onLayoutAnchor = (event: LayoutChangeEvent) => {
      onLayout?.(event);
      const { width, height } = event.nativeEvent.layout;
      setAnchor({ x: width / 2, y: height / 2 - styles.pills.height / 2 });
    };

    // Animate the max width of the button
    const maxWidth = collapsedAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: animatedWidth,
    });

    // Calculate the width of the button based on the layout
    const onLayoutButton = (event: LayoutChangeEvent) => {
      const { width: layoutWidth } = event.nativeEvent.layout;
      setAnimatedWidth([0, layoutWidth + 48]);
    };

    return (
      <Animated.View
        onLayout={onLayoutAnchor}
        ref={ref}
        style={[
          {
            maxWidth: maxWidth,
            opacity: collapsedAnimation,
            transform: [
              { translateX: -anchor.x },
              { translateY: -anchor.y },
              { scale: collapsedAnimation },
              { translateX: anchor.x },
              { translateY: anchor.y },
            ],
          },
          button.style,
        ]}
      >
        <Button
          key={button.label}
          leftElement={
            button.count && button.count > 0 ? (
              <Text style={styles.countText}>{button.count}</Text>
            ) : null
          }
          leftElementStyle={styles.countWrapper}
          onLayout={onLayoutButton}
          onPress={() => button.onPress()}
          rightElement={
            button.dropDown ? (
              <ChevronDown
                fill={button.active ? theme.colors.white : theme.colors.primary}
                stroke={
                  button.active ? theme.colors.white : theme.colors.primary
                }
              />
            ) : null
          }
          rightElementStyle={styles.filterButtonRightElement}
          size="xsmall"
          style={[
            styles.pills,
            {
              backgroundColor: button.active
                ? theme.sk.color.bg.button.primary.default
                : theme.sk.color.bg.secondary.default,
            },
            style,
          ]}
          textStyle={[
            TYPOGRAPHY.pSemiBold,
            styles.pillText,
            {
              color: button.active ? theme.colors.white : undefined,
            },
          ]}
          trackingData={{
            name: `filter_pills.${button.label}`,
          }}
        >
          {button.label}
        </Button>
      </Animated.View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  countText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.white,
    fontSize: 12,
    textAlign: 'center',
  },
  countWrapper: {
    backgroundColor: theme.colors.accent,
    borderRadius: 10,
    height: 16,
    justifyContent: 'center',
    marginRight: 8,
    width: 16,
  },
  filterButtonRightElement: {
    marginLeft: 8,
  },
  pills: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 36,
    borderWidth: 0,
    height: 32,
  },
  pillText: {
    fontSize: 14,
  },
}));
